import { Fragment, useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type {
  AdminStatsPayload,
  PainDiscoveryFunnelStep,
  PainDiscoveryPayload,
  StatsRange,
} from "./apiTypes";

type MagicStatsWithSurvey = AdminStatsPayload & {
  survey?: { id: string; slug: string; name: string };
};

export type PainDiscoveryDashProps = {
  magicToken?: string;
};
import { buildFieldMap, formatFieldValue } from "./formatAnswers";

const FUNNEL_VISUAL: Array<{
  icon: ReactNode;
  accentBorder: string;
  accentBg: string;
  accentGlow: string;
  iconColor: string;
}> = [
  {
    accentBorder: "border-violet-500/45",
    accentBg: "bg-violet-500/[0.12]",
    accentGlow: "shadow-[0_0_24px_-4px_rgba(176,38,255,0.35)]",
    iconColor: "text-violet-400",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
        />
      </svg>
    ),
  },
  {
    accentBorder: "border-fuchsia-500/45",
    accentBg: "bg-fuchsia-500/[0.12]",
    accentGlow: "shadow-[0_0_24px_-4px_rgba(217,70,239,0.3)]",
    iconColor: "text-fuchsia-400",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
        />
      </svg>
    ),
  },
  {
    accentBorder: "border-orange-500/45",
    accentBg: "bg-orange-500/[0.12]",
    accentGlow: "shadow-[0_0_24px_-4px_rgba(249,115,22,0.3)]",
    iconColor: "text-orange-400",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
  {
    accentBorder: "border-sky-500/45",
    accentBg: "bg-sky-500/[0.12]",
    accentGlow: "shadow-[0_0_24px_-4px_rgba(14,165,233,0.3)]",
    iconColor: "text-sky-400",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655-5.653a2.548 2.548 0 010-3.586L11.42 15.17z"
        />
      </svg>
    ),
  },
  {
    accentBorder: "border-emerald-500/45",
    accentBg: "bg-emerald-500/[0.12]",
    accentGlow: "shadow-[0_0_24px_-4px_rgba(52,211,153,0.3)]",
    iconColor: "text-emerald-400",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    accentBorder: "border-pp-blue/55",
    accentBg: "bg-gradient-to-br from-pp-violet/15 to-pp-blue/15",
    accentGlow: "shadow-[0_0_28px_-6px_rgba(45,124,255,0.4)]",
    iconColor: "text-pp-blue",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
        />
      </svg>
    ),
  },
];

function FunnelConnector() {
  return (
    <div className="hidden shrink-0 self-center sm:flex items-center justify-center w-3 text-pp-violet/45" aria-hidden>
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.25}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
      </svg>
    </div>
  );
}

function ValidationFunnelHorizontal({ steps }: { steps: PainDiscoveryFunnelStep[] }) {
  const maxCount = Math.max(1, ...steps.map((s) => s.count));

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-nowrap sm:items-stretch sm:gap-1"
      role="list"
      aria-label="Embudo de validación"
    >
      {steps.map((step, i) => {
        const vis = FUNNEL_VISUAL[i] ?? FUNNEL_VISUAL[FUNNEL_VISUAL.length - 1];
        const barPct = Math.round((step.count / maxCount) * 100);
        return (
          <Fragment key={step.id}>
            <div
              className={`flex min-w-0 flex-1 flex-col rounded-xl border ${vis.accentBorder} ${vis.accentBg} ${vis.accentGlow} p-2 sm:p-1.5 transition-transform sm:hover:-translate-y-0.5`}
              role="listitem"
            >
              <div
                className={`mx-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-pp-card/90 ${vis.iconColor} shadow-inner`}
              >
                {vis.icon}
              </div>
              <p className="mt-1.5 text-center text-lg font-bold tabular-nums text-pp-main leading-none">
                {step.count}
              </p>
              <p className="mt-1 text-center text-[10px] sm:text-[11px] font-medium text-pp-main leading-tight line-clamp-3 min-h-[2.25rem] sm:min-h-[2.5rem]">
                {step.label}
              </p>
              <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-pp-soft/80">
                <div
                  className="h-full rounded-full bg-pp-gradient transition-all duration-500"
                  style={{ width: `${barPct}%` }}
                />
              </div>
              <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                <span
                  className="rounded-full bg-pp-card/90 px-1 py-px text-[9px] font-mono text-pp-muted border border-pp-border"
                  title="Porcentaje sobre la base (todas las respuestas en ventana)"
                >
                  {step.pct_of_base}%
                </span>
                {step.pct_of_previous != null && i > 0 && (
                  <span
                    className="rounded-full bg-pp-violet/10 px-1 py-px text-[9px] font-mono text-pp-violet border border-pp-violet/25"
                    title="Retención respecto al paso anterior del embudo"
                  >
                    {step.pct_of_previous}%
                  </span>
                )}
              </div>
            </div>
            {i < steps.length - 1 && <FunnelConnector />}
          </Fragment>
        );
      })}
    </div>
  );
}

function level0to10(v: number): string {
  if (v >= 7.5) return "Alta";
  if (v >= 5) return "Media";
  return "Baja";
}

/** Misma pista que Validation Score: track `bg-pp-soft` + relleno `bg-pp-gradient`; escala 0–10 → ancho en %. */
function DimBar({ label, value }: { label: string; value: number }) {
  const widthPct = Math.min(100, Math.max(0, (value / 10) * 100));
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="uppercase tracking-wide text-pp-muted">{label}</span>
        <span className="font-mono text-pp-main">
          {value.toFixed(1)}/10 · {level0to10(value)}
        </span>
      </div>
      <div className="h-2.5 rounded bg-pp-soft overflow-hidden">
        <div className="h-full bg-pp-gradient rounded transition-all" style={{ width: `${widthPct}%` }} />
      </div>
    </div>
  );
}

function outcomeLabel(o: PainDiscoveryPayload["strategic_engine"]["outcome"]): string {
  switch (o) {
    case "INVEST":
      return "INVERTIR";
    case "PIVOT":
      return "PIVOTAR";
    case "DISCARD":
      return "DESCARTAR";
    default:
      return "SEGUIR ENTREVISTANDO";
  }
}

export default function PainDiscoveryDash(props?: PainDiscoveryDashProps) {
  const { magicToken } = props ?? {};
  const [searchParams] = useSearchParams();
  const surveyId = searchParams.get("survey_id") ?? "";
  const [range, setRange] = useState<StatsRange>("1m");
  const [stats, setStats] = useState<AdminStatsPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const q = new URLSearchParams({ range });
      let res: Response;
      if (magicToken) {
        res = await fetch(`/api/backoffice/magic/${encodeURIComponent(magicToken)}/stats?${q.toString()}`);
      } else {
        if (surveyId) q.set("survey_id", surveyId);
        res = await fetch(`/api/admin/stats?${q.toString()}`, { credentials: "include" });
      }
      if (!res.ok) {
        setErr(magicToken ? "Link inválido o expirado." : "No se pudieron cargar datos.");
        return;
      }
      setStats((await res.json()) as AdminStatsPayload);
    } catch {
      setErr("Error de red.");
    }
  }, [range, surveyId, magicToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const fm = buildFieldMap();
  const q6Field = fm.get("q6_what_breaks");

  if (!stats) {
    return <p className="text-sm text-pp-muted">{err ?? "Cargando…"}</p>;
  }

  const pd = stats.pain_discovery;
  if (!pd) {
    return (
      <div className="pp-card p-6">
        <p className="text-sm text-pp-muted">
          Este despliegue no incluye el bloque pain_discovery. Actualizá el Worker y recargá.
        </p>
      </div>
    );
  }

  const { funnel, core_dimensions, top_problems, pain_score, strategic_engine } = pd;
  const w = pain_score.formula_weights;
  const isFlex = stats.stats_template === "flexible_v1";
  const flexDims = stats.flexible_dimensions ?? [];

  const magicSurvey =
    magicToken && stats ? (stats as MagicStatsWithSurvey).survey : undefined;

  return (
    <div className="space-y-8">
      {err && <p className="text-sm text-rose-600">{err}</p>}

      {magicSurvey && (
        <div className="rounded-xl border border-pp-border bg-pp-card px-4 py-3 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-pp-quiet">Encuesta</p>
          <p className="text-lg font-semibold text-pp-main">{magicSurvey.name}</p>
          <p className="text-xs text-pp-muted font-mono mt-0.5">{magicSurvey.slug}</p>
        </div>
      )}

      {isFlex && !magicToken && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.08] px-4 py-3 text-sm text-pp-main">
          <p className="font-medium">Encuesta flexible</p>
          <p className="mt-1 text-xs text-pp-muted leading-relaxed">
            El embudo anidado P2 de Vínculos no aplica. Abajo ves respuestas en la ventana y las dimensiones declaradas
            en la definición. Para el veredicto 4 barras + coach, usá{" "}
            <Link
              className="text-sky-700 underline font-medium"
              to={`/admin/new-dash${surveyId ? `?survey_id=${encodeURIComponent(surveyId)}` : ""}`}
            >
              Validation Score
            </Link>{" "}
            (menú Legacy).
          </p>
        </div>
      )}

      {isFlex && magicToken && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.08] px-4 py-3 text-sm text-pp-main">
          <p className="font-medium">Encuesta flexible</p>
          <p className="mt-1 text-xs text-pp-muted leading-relaxed">
            Embudo y dimensiones según la definición de esta encuesta (acceso por enlace temporal).
          </p>
        </div>
      )}

      <header>
        <h1 className="text-2xl font-bold tracking-tight text-pp-main">Mom Test / Evidencia</h1>
        <p className="mt-1 text-sm text-pp-muted leading-relaxed">
          {isFlex ? (
            <>
              <span className="font-semibold text-pp-main">N = {stats.p2_count}</span> respuestas completas en la ventana{" "}
              <span className="font-mono">{stats.range}</span>
              {magicToken ? " · magic link" : surveyId ? " · encuesta filtrada" : ""}. En encuestas flexibles no existe split P2 vs P2n:
              eso es del flujo Vínculos; acá cada fila es un envío completo del cuestionario.
            </>
          ) : (
            <>
              Cohorte P2+P2n · {stats.p2_count} respuestas · ventana {stats.range}
              {magicToken ? " · magic link" : surveyId ? " · encuesta filtrada" : ""}
            </>
          )}
        </p>
      </header>

      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div
          className="inline-flex rounded-xl border border-pp-border bg-pp-soft p-0.5"
          role="group"
          aria-label="Ventana de tiempo"
        >
          {(["1d", "1w", "1m", "all"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setRange(v)}
              className={`px-3 py-1.5 text-xs font-mono rounded-lg transition-colors ${
                range === v ? "bg-pp-gradient text-white shadow-sm" : "text-pp-muted hover:bg-pp-card"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <section className="rounded-2xl border border-pp-border bg-pp-card p-5 md:p-6 shadow-ppCard overflow-hidden">
        <h2 className="text-lg font-bold text-pp-main">1. Validation funnel</h2>
        <p className="mt-1 text-xs text-pp-muted max-w-2xl">
          {isFlex
            ? "Embudo declarativo en la definición (analytics.funnel): cada paso filtra sobre el anterior. Solo hay envíos completos; no medimos abandono a mitad de formulario."
            : "Embudo anidado (cada paso filtra al anterior). Base: todas las respuestas P2+P2n en la ventana. En pantalla ancha los pasos comparten el ancho; en móvil se apilan."}
        </p>
        <div className="mt-6">
          <ValidationFunnelHorizontal steps={funnel} />
        </div>
        <div className="mt-6 rounded-xl border border-pp-border bg-pp-soft/50 p-4 text-sm text-pp-muted space-y-2">
          <p className="font-medium text-pp-main">Interpretación</p>
          {isFlex ? (
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Si la encuesta define <span className="font-mono">analytics.funnel</span>, cada tarjeta es un paso
                acumulativo (cada regla filtra sobre la cohorte anterior).
              </li>
              <li>
                Sin funnel declarado: una sola tarjeta con N (solo envíos completos en la ventana).
              </li>
              <li>Si N es bajo, ampliá la ventana a “all” o esperá más respuestas antes de inferir.</li>
            </ul>
          ) : (
            <ul className="list-disc pl-5 space-y-1">
              <li>Mucha gente puede tener el problema; el filtro fuerte suele estar en urgencia, workaround y WTP.</li>
              <li>El paso “piloto” refleja compromiso sí/tal vez o opt-in de piloto.</li>
              <li>“Pagar en 3 meses” es un proxy (urgencia corta + señal de precio/gasto) hasta tener pregunta dedicada.</li>
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-pp-border bg-pp-card p-4 shadow-ppCard">
        <h2 className="text-lg font-bold text-pp-main mb-3">2. Core dimensions</h2>
        <p className="text-xs text-pp-muted mb-4">
          {isFlex
            ? "Primeras dimensiones declaradas en la encuesta (valores % del motor → escala 0–10 en las barras)."
            : "Promedios 0–10 sobre P2+P2n. Pain = score_pain del modelo; WTP = willingness; Objections = penalización promedio escalada. Barras al estilo Validation Score."}
        </p>
        {isFlex && flexDims.length > 0
          ? flexDims.slice(0, 4).map((d) => (
              <DimBar key={d.id} label={d.label} value={d.value / 10} />
            ))
          : [
              <DimBar key="pain" label="Pain" value={core_dimensions.pain_0_10} />,
              <DimBar key="urg" label="Urgency" value={core_dimensions.urgency_0_10} />,
              <DimBar key="wtp" label="Willingness To Pay (WTP)" value={core_dimensions.wtp_0_10} />,
              <DimBar key="obj" label="Objections" value={core_dimensions.objections_0_10} />,
            ]}
      </section>

      <section className="pp-card p-6 shadow-ppCard">
        <h2 className="text-lg font-bold text-pp-main">3. Top repeated problems (q6)</h2>
        {isFlex && (
          <p className="mt-1 text-xs text-pp-muted">
            Sin q6 en encuestas flexibles; revisá distribuciones en Análisis o respuestas crudas en Respuestas.
          </p>
        )}
        <div className="mt-4 overflow-x-auto rounded-xl border border-pp-border">
          <table className="min-w-full text-sm">
            <thead className="pp-table-head">
              <tr>
                <th className="p-2 text-left">Problem</th>
                <th className="p-2 text-right">Mentions</th>
                <th className="p-2 text-left">Severity</th>
              </tr>
            </thead>
            <tbody>
              {top_problems.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-4 text-pp-muted">
                    Sin datos q6 en esta ventana.
                  </td>
                </tr>
              ) : (
                top_problems.map((row) => (
                  <tr key={row.key} className="border-t border-pp-border">
                    <td className="p-2 text-pp-main">{formatFieldValue(q6Field, row.key)}</td>
                    <td className="p-2 text-right font-mono tabular-nums">{row.mentions}</td>
                    <td className="p-2 capitalize">{row.severity}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pp-card p-6 shadow-ppCard">
        <h2 className="text-lg font-bold text-pp-main">4. Pain score (spec)</h2>
        {isFlex ? (
          <p className="mt-2 text-sm text-pp-muted leading-relaxed">
            La fórmula ponderada P2 no aplica a encuestas flexibles. El número de resumen abajo es un promedio simple
            de las cuatro dimensiones mostradas en la sección 2 (solo orientativo).
          </p>
        ) : (
          <pre className="mt-3 p-4 rounded-xl bg-pp-soft text-xs font-mono text-pp-main whitespace-pre-wrap">
            {`Pain Score =\n(Intensity × ${(w.intensity * 100).toFixed(0)}%)\n+ (Frequency × ${(w.frequency * 100).toFixed(0)}%)\n+ (Impact × ${(w.impact * 100).toFixed(0)}%)\n+ (Urgency × ${(w.urgency * 100).toFixed(0)}%)\n+ (Workaround × ${(w.workaround * 100).toFixed(0)}%)`}
          </pre>
        )}
        <p className="mt-4 text-lg font-semibold text-pp-main">
          Pain score global: {pain_score.global_0_10.toFixed(1)} / 10
        </p>
        {!isFlex && (
          <p className="mt-2 text-xs text-pp-muted">
            Intensidad y componentes derivados de los mismos mapas que el scoring P2 (q1–q7, q3, q6).
          </p>
        )}
        <h3 className="mt-6 text-sm font-semibold text-pp-main">By segment</h3>
        <p className="text-xs text-pp-muted mb-2">
          {pain_score.segment_source === "role_field"
            ? `Agrupado por campo ${pain_score.segment_field_id ?? "cq_role"} en respuestas.`
            : "Sin valores en cq_role: se usa segmento de score (bajo/medio/alto)."}
        </p>
        <div className="overflow-x-auto rounded-xl border border-pp-border">
          <table className="min-w-full text-sm">
            <thead className="pp-table-head">
              <tr>
                <th className="p-2 text-left">Segment</th>
                <th className="p-2 text-right">n</th>
                <th className="p-2 text-right">Pain score</th>
              </tr>
            </thead>
            <tbody>
              {pain_score.by_segment.map((row) => (
                <tr key={row.segment} className="border-t border-pp-border">
                  <td className="p-2 text-pp-main">{row.segment}</td>
                  <td className="p-2 text-right font-mono">{row.n}</td>
                  <td className="p-2 text-right font-mono tabular-nums">{row.pain_0_10.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-pp-border bg-pp-card p-4 shadow-ppCard">
        <h2 className="text-lg font-bold text-pp-main">5. Strategic decision engine</h2>
        <p className="mt-3 text-sm text-pp-muted">Opportunity score (heurística)</p>
        <div className="mt-2">
          <DimBar label="Opportunity" value={strategic_engine.opportunity_0_10} />
        </div>
        <p className="mt-4 text-sm text-pp-muted">Suggested outcome</p>
        <p className="text-xl font-bold text-pp-main tracking-tight">{outcomeLabel(strategic_engine.outcome)}</p>
        <p className="mt-1 text-xs font-mono text-pp-quiet">{strategic_engine.outcome}</p>

        <h3 className="mt-6 text-sm font-semibold text-pp-main">Decision rules (v1)</h3>
        <div className="mt-2 overflow-x-auto rounded-xl border border-pp-border">
          <table className="min-w-full text-sm">
            <thead className="pp-table-head">
              <tr>
                <th className="p-2 text-left">Outcome</th>
                <th className="p-2 text-left">Condition</th>
              </tr>
            </thead>
            <tbody className="text-pp-muted">
              <tr className="border-t border-pp-border">
                <td className="p-2 text-pp-main">INVEST</td>
                <td className="p-2">Pain, urgency y WTP ≥ 6; objeciones &lt; 6.5; N ≥ 20.</td>
              </tr>
              <tr className="border-t border-pp-border">
                <td className="p-2 text-pp-main">PIVOT</td>
                <td className="p-2">Pain ≥ 6 pero WTP &lt; 4.5.</td>
              </tr>
              <tr className="border-t border-pp-border">
                <td className="p-2 text-pp-main">DISCARD</td>
                <td className="p-2">N ≥ 20, pain &lt; 4 y ≥ 45% con fricción “nunca”.</td>
              </tr>
              <tr className="border-t border-pp-border">
                <td className="p-2 text-pp-main">KEEP INTERVIEWING</td>
                <td className="p-2">N &lt; 20; o señales contradictorias (p. ej. pain alto + objeciones fuertes + poco HOT).</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
